<template>
  <div class="sketch-circular-pattern-property" data-testid="sketch-circular-pattern-property-section">
    <n-collapse :default-expanded-names="['parameters', 'entities', 'skipped']" class="pm-collapse">
      <n-collapse-item title="参数(P)" name="parameters">
        <div class="group-block">
          <n-checkbox
            :checked="state.parameters.reverseDirection"
            @update:checked="updateParameter('reverseDirection', $event)"
          >
            反向
          </n-checkbox>

          <div class="selection-row">
            <span class="field-label">阵列中心</span>
            <div class="selection-input" @click="activateSelection('center')">
              <SelectionListFrame
                :class="{ 'selection-list-frame--active': state.activeSelectionRole === 'center' }"
                :items="centerItems"
                :active-index="state.activeSelectionRole === 'center' ? 0 : -1"
                :visible-rows="1"
                :context-menu-enabled="false"
                :show-delete-action="false"
                :show-delete-all-action="false"
                item-key-field="id"
                item-label-field="label"
                empty-text="选择中心点"
              />
            </div>
          </div>

          <div class="number-row">
            <span class="field-label">中心 X</span>
            <n-input-number
              :value="centerXMillimeters"
              size="small"
              :precision="8"
              :step="1"
              @update:value="updateCenterCoordinate(0, $event)"
            >
              <template #suffix>mm</template>
            </n-input-number>
          </div>

          <div class="number-row">
            <span class="field-label">中心 Y</span>
            <n-input-number
              :value="centerYMillimeters"
              size="small"
              :precision="8"
              :step="1"
              @update:value="updateCenterCoordinate(1, $event)"
            >
              <template #suffix>mm</template>
            </n-input-number>
          </div>

          <div class="number-row">
            <span class="field-label">总角度</span>
            <n-input-number
              :value="totalAngleDisplay"
              size="small"
              :precision="8"
              :step="1"
              :min="0.001"
              :max="360"
              @update:value="updateNullableNumberParameter('totalAngleDeg', $event)"
            >
              <template #suffix>度</template>
            </n-input-number>
          </div>

          <n-checkbox
            :checked="state.parameters.equalSpacing"
            @update:checked="updateParameter('equalSpacing', $event)"
          >
            等间距(S)
          </n-checkbox>

          <n-checkbox
            :checked="state.parameters.showRadiusDimension"
            @update:checked="updateParameter('showRadiusDimension', $event)"
          >
            标注半径
          </n-checkbox>

          <n-checkbox
            :checked="state.parameters.showSpacingDimension"
            @update:checked="updateParameter('showSpacingDimension', $event)"
          >
            标注角间距(A)
          </n-checkbox>

          <div class="number-row">
            <span class="field-label">实例数</span>
            <n-input-number
              :value="state.parameters.instanceCount"
              size="small"
              :precision="0"
              :step="1"
              :min="2"
              @update:value="updateNullableNumberParameter('instanceCount', $event)"
            />
          </div>

          <n-checkbox
            :checked="state.parameters.showInstanceCount"
            @update:checked="updateParameter('showInstanceCount', $event)"
          >
            显示实例记数(D)
          </n-checkbox>

          <div class="number-row">
            <span class="field-label">半径</span>
            <n-input-number
              :value="radiusMillimeters"
              size="small"
              :precision="8"
              :step="1"
              :min="0.001"
              @update:value="updateRadius($event)"
            >
              <template #suffix>mm</template>
            </n-input-number>
          </div>

          <div class="number-row">
            <span class="field-label">初始角</span>
            <n-input-number
              :value="startAngleDisplay"
              size="small"
              :precision="8"
              :step="1"
              @update:value="updateNullableNumberParameter('startAngleDeg', $event)"
            >
              <template #suffix>度</template>
            </n-input-number>
          </div>

          <div v-if="!state.parameters.equalSpacing" class="number-row">
            <span class="field-label">角间距</span>
            <n-input-number
              :value="spacingAngleDisplay"
              size="small"
              :precision="8"
              :step="1"
              :min="0.001"
              @update:value="updateNullableNumberParameter('spacingAngleDeg', $event)"
            >
              <template #suffix>度</template>
            </n-input-number>
          </div>
        </div>
      </n-collapse-item>

      <n-collapse-item title="要阵列的实体(E)" name="entities">
        <div class="selection-input" @click="activateSelection('entities')">
          <SelectionListFrame
            :class="{ 'selection-list-frame--active': state.activeSelectionRole === 'entities' }"
            :items="state.selectedItems"
            :active-index="state.activeSelectionRole === 'entities' ? 0 : -1"
            :visible-rows="4"
            :context-menu-enabled="true"
            :show-delete-action="true"
            :show-delete-all-action="true"
            item-key-field="id"
            item-label-field="label"
            empty-text="请在视口中选择要阵列的实体"
            @delete-item="deleteSelectedItem"
            @delete-all="deleteAllSelectedItems"
          />
        </div>
      </n-collapse-item>

      <n-collapse-item title="可跳过的实例(I)" name="skipped">
        <div class="skipped-instance-row" @click="activateSelection('skipped-instances')">
          <svg class="skipped-instance-icon" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="3" r="1.6" />
            <circle cx="18.4" cy="5.6" r="1.6" />
            <circle cx="21" cy="12" r="1.6" />
            <circle cx="18.4" cy="18.4" r="1.6" />
            <circle cx="12" cy="21" r="1.6" />
            <circle cx="5.6" cy="18.4" r="1.6" />
            <circle cx="3" cy="12" r="1.6" />
            <circle cx="5.6" cy="5.6" r="1.6" />
          </svg>
          <div class="skipped-instance-selection">
            <SelectionListFrame
              :class="{'selection-list-frame--active': state.activeSelectionRole === 'skipped-instances'}"
              :items="skippedInstanceItems"
              :active-index="-1"
              :visible-rows="1"
              :context-menu-enabled="true"
              :show-delete-action="true"
              :show-delete-all-action="true"
              item-key-field="id"
              item-label-field="label"
              empty-text=""
              @delete-item="deleteSkippedInstanceItem"
              @delete-all="clearSkippedInstances"
            />
          </div>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import type {Subscription} from 'rxjs';
import {NCollapse, NCollapseItem, NCheckbox, NInputNumber} from 'naive-ui';
import {
  convertMToMm,
  convertMmToM,
  roundToEightDecimalPlaces,
  type SketchCircularPatternControllerSnapshot,
  type SketchCircularPatternParameters,
  type SketchCircularPatternSelectionRole,
} from '@puppycad/cad-core';
import type {SketchCircularPatternPropertySectionProps} from '@/types/sketch-circular-pattern';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';
import {useViewer} from '@/hooks';

defineProps<SketchCircularPatternPropertySectionProps>();
const {getViewer, setViewerCallback} = useViewer();

/** 当前控制器快照。 */
const localState = ref<SketchCircularPatternControllerSnapshot>(createDefaultSnapshot());

/** 控制器快照订阅。 */
let snapshotSubscription: Subscription | null = null;

/** Viewer 就绪监听取消函数。 */
let disposeViewerReadyCallback: (() => void) | null = null;

/** 当前面板状态。 */
const state = computed(() => localState.value);

/** 阵列中心列表。 */
const centerItems = computed(() => state.value.centerItem ? [state.value.centerItem] : []);

/** 已跳过实例列表项。 */
const skippedInstanceItems = computed(() => state.value.parameters.skippedInstanceIndexes.map((instanceIndex) => ({
  id: `sketch-circular-pattern-skipped-${instanceIndex}`,
  label: `实例 ${instanceIndex + 1}`,
  instanceIndex,
})));

/** 阵列中心 X，单位毫米。 */
const centerXMillimeters = computed(() => roundToEightDecimalPlaces(
  convertMToMm(state.value.parameters.center2d[0]),
));

/** 阵列中心 Y，单位毫米。 */
const centerYMillimeters = computed(() => roundToEightDecimalPlaces(
  convertMToMm(state.value.parameters.center2d[1]),
));

/** 阵列半径，单位毫米。 */
const radiusMillimeters = computed(() => roundToEightDecimalPlaces(
  convertMToMm(state.value.parameters.radius),
));

/** 阵列总角度显示值，单位为度。 */
const totalAngleDisplay = computed(() => roundToEightDecimalPlaces(
  state.value.parameters.totalAngleDeg,
));

/** 阵列初始角显示值，单位为度。 */
const startAngleDisplay = computed(() => roundToEightDecimalPlaces(
  state.value.parameters.startAngleDeg,
));

/** 阵列角间距显示值，单位为度。 */
const spacingAngleDisplay = computed(() => roundToEightDecimalPlaces(
  state.value.parameters.spacingAngleDeg,
));

/**
 * 创建面板默认快照。
 *
 * @returns 未激活的默认快照。
 */
function createDefaultSnapshot(): SketchCircularPatternControllerSnapshot {
  return {
    active: false,
    activeSelectionRole: 'entities',
    selectedItems: [],
    centerItem: null,
    parameters: {
      center2d: [0, 0],
      radius: 0.1,
      startAngleDeg: 0,
      totalAngleDeg: 360,
      equalSpacing: true,
      spacingAngleDeg: 90,
      reverseDirection: false,
      instanceCount: 4,
      rotateInstances: true,
      showRadiusDimension: false,
      showSpacingDimension: false,
      showInstanceCount: true,
      skippedInstanceIndexes: [],
    },
    canConfirm: false,
  };
}

/**
 * 绑定控制器快照。
 */
function bindSnapshot(): void {
  snapshotSubscription?.unsubscribe();
  snapshotSubscription = getViewer()?.observeSketchCircularPatternSnapshot?.().subscribe((snapshot) => {
    localState.value = snapshot;
  }) ?? null;
  localState.value = getViewer()?.getSketchCircularPatternSnapshot?.() ?? createDefaultSnapshot();
}

onMounted(() => {
  bindSnapshot();
  disposeViewerReadyCallback = setViewerCallback(bindSnapshot);
});

onBeforeUnmount(() => {
  snapshotSubscription?.unsubscribe();
  snapshotSubscription = null;
  disposeViewerReadyCallback?.();
  disposeViewerReadyCallback = null;
});

/**
 * 激活选择角色。
 *
 * @param role 选择角色。
 */
function activateSelection(role: SketchCircularPatternSelectionRole): void {
  getViewer()?.setSketchCircularPatternSelectionRole?.(role);
}

/**
 * 更新布尔或数值参数。
 *
 * @param key 参数键。
 * @param value 参数值。
 */
function updateParameter<K extends keyof SketchCircularPatternParameters>(
  key: K,
  value: SketchCircularPatternParameters[K],
): void {
  getViewer()?.updateSketchCircularPatternParameters?.({
    [key]: value,
  } as Partial<SketchCircularPatternParameters>);
}

/**
 * 更新允许空输入的数值参数。
 *
 * @param key 数值参数键。
 * @param value 输入值。
 */
function updateNullableNumberParameter(
  key: 'totalAngleDeg' | 'instanceCount' | 'startAngleDeg' | 'spacingAngleDeg',
  value: number | null,
): void {
  if (value !== null) {
    getViewer()?.updateSketchCircularPatternParameters?.({[key]: value});
  }
}

/**
 * 更新阵列中心坐标。
 *
 * @param axisIndex 坐标索引。
 * @param value 毫米输入值。
 */
function updateCenterCoordinate(axisIndex: 0 | 1, value: number | null): void {
  if (value === null) {
    return;
  }
  const center2d: [number, number] = [
    state.value.parameters.center2d[0],
    state.value.parameters.center2d[1],
  ];
  center2d[axisIndex] = convertMmToM(value);
  getViewer()?.updateSketchCircularPatternParameters?.({center2d});
}

/**
 * 更新阵列半径。
 *
 * @param value 毫米输入值。
 */
function updateRadius(value: number | null): void {
  if (value !== null) {
    getViewer()?.updateSketchCircularPatternParameters?.({radius: convertMmToM(value)});
  }
}

/**
 * 删除一个源实体选择项。
 *
 * @param payload 列表删除载荷。
 */
function deleteSelectedItem(payload: {item: unknown; index: number}): void {
  const item = payload.item as {id?: unknown};
  if (typeof item.id === 'string') {
    getViewer()?.removeSketchCircularPatternSelection?.(item.id);
  }
}

/**
 * 清空所有源实体选择。
 */
function deleteAllSelectedItems(): void {
  getViewer()?.clearSketchCircularPatternSelections?.();
}

/**
 * 恢复一个已跳过实例。
 *
 * @param instanceIndex 稳定实例索引。
 */
function restoreSkippedInstance(instanceIndex: number): void {
  getViewer()?.restoreSketchCircularPatternSkippedInstance?.(instanceIndex);
}

/**
 * 从列表恢复单个已跳过实例。
 *
 * @param payload 列表删除载荷。
 */
function deleteSkippedInstanceItem(payload: {item: unknown; index: number}): void {
  const item = payload.item as {instanceIndex?: unknown};
  if (typeof item.instanceIndex === 'number') {
    restoreSkippedInstance(item.instanceIndex);
  }
}

/**
 * 清空全部跳过实例。
 */
function clearSkippedInstances(): void {
  getViewer()?.updateSketchCircularPatternParameters?.({skippedInstanceIndexes: []});
}
</script>

<style scoped>
.sketch-circular-pattern-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.pm-collapse :deep(.n-collapse-item__content-inner) {
  padding-top: 4px;
}

.group-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selection-row,
.number-row {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  align-items: center;
  gap: 6px;
}

.field-label {
  color: var(--n-text-color);
  font-size: 12px;
  text-align: right;
  white-space: nowrap;
}

.selection-input,
.number-row :deep(.n-input-number) {
  min-width: 0;
}

.selection-input {
  cursor: pointer;
}

.number-row :deep(.n-input__suffix) {
  font-size: 12px;
}

.selection-list-frame--active {
  border-color: var(--n-primary-color, var(--puppy-primary));
  background: color-mix(in srgb, var(--n-primary-color, var(--puppy-primary)) 8%, transparent);
}

.skipped-instance-row {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  align-items: start;
  gap: 6px;
}

.skipped-instance-icon {
  width: 20px;
  height: 20px;
  margin: 4px 2px 0;
  fill: var(--n-primary-color, var(--puppy-primary));
}

.skipped-instance-selection {
  min-width: 0;
  cursor: pointer;
}
</style>
